import { ProppingSystem } from '@inter-ikea-core/core';
import HelpLinesOuterMeasurementsWidget from 'components/HelpLines/HelpLinesOuterMeasurementsWidget';
import HelpLinesWidget from 'components/HelpLines/HelpLinesWidget';
import NewMeasurementsWidget from 'components/MeasurementsWidget/NewMeasurementsWidget';
import { plannerRenderSettings } from 'config/plannerRenderSettings';
import { useCore } from 'contexts/core';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { useSceneHandler } from 'contexts/sceneHandler';
import { useServiceSettings } from 'contexts/serviceSettings';
import { useAppDispatch } from 'ducks/hooks';
import {
  hideMeasurements,
  isBigScreenPlanningEnabled,
  isProppingVisible,
  setBigScreenPlanningDisabled,
  setBigScreenPlanningEnabled,
  showMeasurements
} from 'ducks/layout';
import { setShowDrawerAccessoriesInfoMessage } from 'ducks/products';
import { setUnsavedChangesSheetDimissed } from 'ducks/sheets';
import { Feature, UsesViewer } from 'features/Feature';
import useDragClipping from 'hooks/useDragClipping';
import useResetPlanner from 'hooks/useResetPlanner';
import useSentryTags from 'hooks/useSentryTags';
import { DraggableProduct } from 'products/interfaces/DraggableProduct';
import { FeatureConfig } from 'rangeConfig/@types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DropTargetMonitor, useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { Route, useHistory } from 'react-router-dom';
import { AppRoutes } from 'routes';
import styled from 'styled-components';
import media from 'ui/media';
import theme from 'ui/theme';
import { e2e, getKvadratFeatures, getQuery } from 'utils/queryUtils';
import { isMobile, isProduction } from '../../utils/platform';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import MeasurementsWidget from '../MeasurementsWidget/MeasurementsWidget';
import { isKiosk } from 'utils/platform';
import DistanceMeasurementWidget from 'components/DistanceMeasurementWidget/DistanceMeasurementWidget';
import { useTranslations } from 'hooks/useTranslations';
import { useBehaviorTracker } from 'contexts/behaviors';
import { useHotkeys } from 'react-hotkeys-hook';
import { initBigScreenPlanner } from './BigScreenPlanner';
import { setHelpLinesLabelsVisible } from 'ducks/helpLines';
import { useIntl } from 'react-intl';
import useSpacePostMessage from 'hooks/useSpacePostMessage';
import { isSpaceEnabled } from 'utils/integration';

export interface SceneEventArgs {
  viewer: any;
  canvas: HTMLCanvasElement;
}

const DropTarget = styled.div`
  position: relative;
  flex-grow: 1;
  flex-shrink: 1;
  height: 100%;
`;

const StyledCanvas = styled.canvas<{ fullscreen: boolean }>`
  width: ${props => (props.fullscreen ? '100%' : `calc(100% - ${theme.layout.sidebarWidth})`)};

  /* Adding the calc inside the media.mobile with the theme variable doesn't seem to be supported by some of the browsers, that's why it's added here instead.
  Google it if interested. Seems to work if I add calc(100% - 140px) but not with the variable theme.layout.mobileMenuHeight, not sure exactly why... */
  height: ${isMobile() ? `calc(100% - ${theme.layout.mobileMenuHeight})` : '100%'};
  user-select: none;
  touch-action: none;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0); /* mobile webkit */
  outline: none;
  :focus {
    outline: 0;
  }

  ${media.mobile`
    width: 100%;
  `}
`;

const NewSolution = () => {
  const coreApi = useCore();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { plannerConfig } = usePlannerConfig();
  const { clearLocalScene } = useSceneHandler();
  const enableSpacePlanning = isSpaceEnabled();

  useResetPlanner();

  useEffect(() => {
    dispatch(setShowDrawerAccessoriesInfoMessage(true));

    clearLocalScene();

    history.replace(AppRoutes.PLANNER);

    dispatch(setUnsavedChangesSheetDimissed(false));
  }, [
    clearLocalScene,
    coreApi,
    dispatch,
    enableSpacePlanning,
    history,
    plannerConfig.productGroups
  ]);

  return null;
};

const useTogglePropping = () => {
  const coreApi = useCore();
  const proppingVisible = useSelector(isProppingVisible);
  const active = e2e() ? false : proppingVisible;

  useEffect(() => {
    active
      ? coreApi.getSystem<ProppingSystem>('ProppingSystem')?.enable()
      : coreApi.getSystem<ProppingSystem>('ProppingSystem')?.disable();
  }, [active, coreApi]);
};

const Scene = () => {
  const canvas: any = useRef(null);
  const { plannerConfig } = usePlannerConfig();
  const coreApi = useCore();
  const {
    dexfEndpoints,
    kompisSettings: { applicationName, locale, dexfApiKey }
  } = useServiceSettings();
  const behaviorTracker = useBehaviorTracker();
  const accept = ['product', 'productCluster'];
  const [draggedItem, setDraggedItem] = useState();
  useDragClipping(coreApi, canvas, draggedItem);
  const enableSpacePlanning = isSpaceEnabled();
  const { useMetric } = useTranslations();
  const screenWidth = getQuery().get('screenWidth');
  const screenHeight = getQuery().get('screenHeight');
  const { formatMessage } = useIntl();

  const dispatch = useDispatch();

  useSpacePostMessage(canvas);

  const {
    plannerConfig: { cameraBehavior: CameraBehavior }
  } = usePlannerConfig();
  const [, drop] = useDrop({
    accept,
    drop: (item: DraggableProduct, monitor: DropTargetMonitor) => {
      if (!canvas.current) return;
      const canvasBoundingRect = canvas.current.getBoundingClientRect();
      const coords = monitor.getClientOffset();
      if (!coords) return;
      const x = coords.x - canvasBoundingRect.left;
      const y = coords.y - canvasBoundingRect.top;

      behaviorTracker.withContext(item.params).trigger(
        'productDropped',
        {
          ...item,
          position: { x, y }
        },
        item.behaviors
      );
    },
    hover: (item: DraggableProduct, monitor: DropTargetMonitor) => {
      setDraggedItem(monitor.getItem());
    }
  });

  // Initialize sentry tags when loading the scene
  useSentryTags();

  useEffect(() => {
    if (!canvas.current) {
      return;
    }

    coreApi.createViewer(
      canvas.current,
      plannerRenderSettings,
      applicationName,
      locale,
      dexfEndpoints.baseUrl,
      dexfApiKey,
      isProduction(),
      getKvadratFeatures(),
      plannerConfig.roomOptions
    );

    if (!enableSpacePlanning) {
      coreApi.kvadratRoom?.createRoom(
        coreApi.ipexViewer,
        plannerRenderSettings,
        canvas.current,
        applicationName,
        locale,
        dexfEndpoints.baseUrl,
        dexfApiKey,
        isProduction() ? 'prod' : 'qa',
        coreApi.core,
        useMetric,
        isKiosk(),
        getKvadratFeatures(),
        plannerConfig.roomOptions
      );

      coreApi.lights?.setupEnvironmentmapValues();
      coreApi.kvadratRoom?.setInitialBlueprint();
    }

    coreApi.lights?.setupSceneLights();
    const centerPosition = coreApi.kvadratRoom?.getRoomCenterPosition();

    // Center lights to room if room is loaded
    if (centerPosition) {
      coreApi.lights?.centerLights(centerPosition);
    }

    plannerConfig.features.forEach((f: FeatureConfig) => {
      if ('onViewerCreated' in f.feature) {
        (f.feature as Feature & UsesViewer).onViewerCreated(coreApi);
      }
    });

    const onResize = () => {
      // Resize the canvas to fit the parent container
      // This is needed to make sure the canvas is correctly sized when the window is resized
      // We need to make sure the canvas width and height are greater than 0 to avoid errors
      if (canvas.current.clientWidth > 0 && canvas.current.clientHeight > 0) {
        coreApi.ipexViewer.resize(canvas.current.clientWidth, canvas.current.clientHeight);
      }
    };

    const onWheel = (e: any) => {
      e.stopPropagation();
      e.preventDefault();
    };

    const c = canvas.current;

    // Stop zooming in canvas from zooming whole browser window
    c.addEventListener('wheel', onWheel, { passive: false });

    window.addEventListener('resize', onResize);

    // save initial camera position to reposition for screenshot

    return () => {
      c?.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    applicationName,
    canvas,
    coreApi,
    dexfApiKey,
    dexfEndpoints.baseUrl,
    locale,
    plannerConfig.features,
    plannerConfig.roomOptions
  ]);

  // show/hide propping when redux state changes
  useTogglePropping();

  const initBigScreenMode = () => {
    if (!isBigScreen) {
      const width = screenWidth && parseFloat(screenWidth);
      const height = screenHeight && parseFloat(screenHeight);

      // These props must be set to init big screen planning
      if (!width || !height) return;

      initBigScreenPlanner(coreApi.kvadratRoom!.ipexViewer, width, height);
      dispatch(hideMeasurements());
      dispatch(setHelpLinesLabelsVisible(false));
      dispatch(setBigScreenPlanningEnabled());
    }
  };

  const isBigScreen = useSelector(isBigScreenPlanningEnabled);
  const exitBigScreenMode = useCallback(() => {
    if (isBigScreen) {
      coreApi.kvadratRoom!.kvadrat!.close();
      dispatch(showMeasurements());
      dispatch(setHelpLinesLabelsVisible(true));
      dispatch(setBigScreenPlanningDisabled());
    }
  }, [isBigScreen, coreApi.kvadratRoom, dispatch]);

  useHotkeys('ctrl+shift+l', initBigScreenMode, [isBigScreen]);
  useHotkeys('cmd+shift+l', initBigScreenMode, [isBigScreen]);
  useHotkeys('esc', exitBigScreenMode, [isBigScreen]);

  return (
    <DropTarget ref={drop} dir="ltr">
      <CameraBehavior />
      <LoadingIndicator />
      {enableSpacePlanning ? <NewMeasurementsWidget /> : <MeasurementsWidget />}
      <DistanceMeasurementWidget />
      <HelpLinesWidget />
      <HelpLinesOuterMeasurementsWidget />
      <StyledCanvas
        data-testid="canvas"
        ref={canvas}
        fullscreen={isBigScreen || isMobile()}
        aria-label={formatMessage({ id: 'storageone.canvasLabel' })}
      />
      <Route path={AppRoutes.PLANNER_NEW} component={NewSolution} />
    </DropTarget>
  );
};

export default Scene;
